import {Injectable, NgZone, inject} from '@angular/core';
import {ChatService} from '../../components/chatBIC/service/chat.service';

/**
 * Kopplung zwischen dem aktiven myCheck-Editor (KI, Hybrid, IDS) und dem Edit-Chat (#2789): Die
 * Review-Leiste im chatBIC-Panel zählt die offenen ✓/✗-Entscheidungen eines chatBIC-Vorschlags,
 * springt per Stepper von Änderung zu Änderung und löst „Alle übernehmen/verwerfen“ aus; der
 * Kontext-Chip oben im Chat nennt den myCheck und das gewählte Element.
 *
 * Pull statt Push: Der Editor registriert sich als {@link ProposalReviewHost}; Leiste und Chip lesen
 * den Zustand bei jeder Change Detection über primitive Getter. Ein Push bei jeder Mutation würde an
 * den Dutzenden Mutationspunkten je Editor leicht vergessen. Templates binden deshalb nie frisch
 * gebaute Objekte aus diesem Service (sonst NG0100 im Dev-Modus).
 *
 * Zustand nur im Browser je Tab, kein Server-Zustand.
 */

export interface ProposalReviewItem {
  /** stabil, z. B. 'field:configJson.kiModel', 'row:tags:3', 'rule:<id>:configJson.parameterValues:<paramId>',
   *  'rule-added:<key>', 'rule-removed:<id>', 'info.title', 'spec:<diffKey>:<path>' */
  key: string;
  /** wählt Abschnitt/Regel/Spezifikation/Tab und scrollt danach zu [data-proposal-review="<key>"] (falls vorhanden) */
  reveal: () => void;
}

export interface ProposalReviewSnapshot {
  proposalId: string;
  /** geordnet wie die Navigation; length === Zahl offener ✓/✗-Entscheidungen */
  items: ProposalReviewItem[];
  /** MyCheckProposal.stale */
  stale: boolean;
  /** Übernehmen/Verwerfen bzw. Speichern läuft */
  busy: boolean;
}

export interface ProposalReviewContext {
  mycheckName: string;
  itemLabel?: string;
}

export interface ProposalReviewHost {
  /** KI/Hybrid: Set-ID, IDS: Regel-ID (wie mycheckProposalReceived) */
  readonly reviewRulesetId: string;

  /** null = kein offener Vorschlag */
  proposalReviewSnapshot(): ProposalReviewSnapshot | null;

  /** für den Kontext-Chip, auch ohne Vorschlag */
  proposalReviewContext(): ProposalReviewContext | null;

  /** = onProposalApplyClicked() */
  acceptAllProposalChanges(): void;

  /** = onProposalDiscardClicked() */
  discardAllProposalChanges(): void;
}

/**
 * Scrollt nach dem nächsten Rendern zum Element mit `data-proposal-review="<key>"`. Gibt es keins
 * (z. B. Abschnitt ohne einzelnes Feld), scrollt er nicht – das ist kein Fehler, der Host hat den
 * Abschnitt dann schon gewählt.
 */
export function revealProposalTarget(key: string): void {
  setTimeout(() => {
    document.querySelector('[data-proposal-review="' + CSS.escape(key) + '"]')
      ?.scrollIntoView({block: 'center', behavior: 'smooth'});
  }, 0);
}

const NOT_REVEALED = -1;

@Injectable({providedIn: 'root'})
export class ProposalReviewService {
  private readonly chatService = inject(ChatService);
  private readonly zone = inject(NgZone);

  private host: ProposalReviewHost | null = null;
  /** 0-basierter Index der zuletzt angesteuerten Änderung; {@link NOT_REVEALED}, solange der Stepper bei
   *  diesem Vorschlag noch nicht benutzt wurde (die Leiste zeigt dann „1 / N“, der erste Klick auf ›
   *  steuert Änderung 1 an, der erste auf ‹ die letzte). */
  private index = NOT_REVEALED;
  private lastProposalId: string | null = null;
  /**
   * Snapshot des Hosts für die laufende Change Detection. Leiste, Banner und Chat-View lesen je CD-Lauf
   * mehrere Getter; ohne Cache baute jeder den Snapshot neu (Regeln sortieren, Map-Diffs). Der Cache gilt
   * bis zum nächsten Microtask – der läuft vor jedem neuen Event, also vor jeder möglichen Mutation – und
   * wird nach jedem Aufruf in den Host sofort verworfen. Der Reset-Microtask läuft bewusst AUSSERHALB der
   * Angular-Zone: in der Zone löste er nach jedem CD-Lauf über `onMicrotaskEmpty` den nächsten aus, der den
   * Cache wieder füllte – eine endlose CD-Schleife, sobald die Leiste im Edit-Chat gerendert wird.
   */
  private cached: { snapshot: ProposalReviewSnapshot | null } | null = null;

  /** Meldet den aktiven Editor an. Die zurückgegebene Funktion meldet ihn wieder ab – aber nur, wenn
   *  inzwischen kein anderer Editor übernommen hat (Navigation Editor → Editor). */
  register(host: ProposalReviewHost): () => void {
    this.host = host;
    this.index = NOT_REVEALED;
    this.lastProposalId = null;
    this.invalidate();
    return () => {
      if (this.host === host) {
        this.host = null;
        this.index = NOT_REVEALED;
        this.lastProposalId = null;
        this.invalidate();
      }
    };
  }

  openCount(): number {
    return this.snapshot()?.items.length ?? 0;
  }

  /** 1-basiert, auf [1, openCount] geklemmt; 0 wenn openCount 0. */
  position(): number {
    const count = this.openCount();
    if (count === 0) return 0;
    return this.index === NOT_REVEALED ? 1 : this.index + 1;
  }

  stale(): boolean {
    return this.snapshot()?.stale ?? false;
  }

  busy(): boolean {
    return this.snapshot()?.busy ?? false;
  }

  /** '<mycheckName> · <itemLabel>' bzw. nur '<mycheckName>'; null ohne Host. */
  contextText(): string | null {
    const context = this.host?.proposalReviewContext();
    if (!context || !context.mycheckName) return null;
    return context.itemLabel ? `${context.mycheckName} · ${context.itemLabel}` : context.mycheckName;
  }

  /** Ob die Review-Leiste gerade im Edit-Chat zu sehen ist (dann entfällt das Banner im Editor).
   *  `floatingChatOpen` zuerst: Spec-Mocks des ChatService haben kein `session()`. */
  isShownInChat(): boolean {
    return !!this.chatService.floatingChatOpen
      && this.chatService.activeSurface === 'edit'
      && !!this.chatService.session('edit').currentChat
      && this.host !== null;
  }

  next(): void {
    this.step(1);
  }

  prev(): void {
    this.step(-1);
  }

  acceptAll(): void {
    if (!this.host || this.busy()) return;
    this.host.acceptAllProposalChanges();
    this.invalidate();
  }

  discardAll(): void {
    if (!this.host || this.busy()) return;
    this.host.discardAllProposalChanges();
    this.invalidate();
  }

  private step(delta: number): void {
    const snapshot = this.snapshot();
    const count = snapshot?.items.length ?? 0;
    if (!snapshot || count === 0) return;
    if (this.index === NOT_REVEALED) {
      this.index = delta > 0 ? 0 : count - 1;
    } else {
      this.index = (this.index + delta + count) % count;
    }
    snapshot.items[this.index].reveal();
    this.invalidate();
  }

  private snapshot(): ProposalReviewSnapshot | null {
    if (this.cached) return this.cached.snapshot;
    const snapshot = this.readSnapshot();
    this.cached = {snapshot};
    this.zone.runOutsideAngular(() => queueMicrotask(() => this.cached = null));
    return snapshot;
  }

  private invalidate(): void {
    this.cached = null;
  }

  /** Liest den Snapshot des Hosts und hält den Index konsistent: neuer Vorschlag → wieder „noch nicht
   *  angesteuert“, weniger offene Änderungen → auf den neuen Bereich klemmen. */
  private readSnapshot(): ProposalReviewSnapshot | null {
    const snapshot = this.host?.proposalReviewSnapshot() ?? null;
    if (!snapshot) {
      this.index = NOT_REVEALED;
      this.lastProposalId = null;
      return null;
    }
    if (snapshot.proposalId !== this.lastProposalId) {
      this.lastProposalId = snapshot.proposalId;
      this.index = NOT_REVEALED;
    }
    const count = snapshot.items.length;
    if (this.index >= count) {
      this.index = Math.max(0, count - 1);
    }
    return snapshot;
  }
}
